// Mozzeno report tabs - Mozzeno's counterpart of LendReportSections.js: a
// fixed-shape report over mozzeno_performance_report (an existing,
// separately-populated collection - see generate_platform_report.js's
// fetchMozzenoReportDoc), translated from the "Mozzeno Performance Report"
// PBIX. Every section reads the doc's own pre-computed fields as-is - the
// same tables the PBIX's Power Query expands from the same documents - so
// there is no new calculation here, only the PBIX's own display transforms
// (relabels, "nan" -> "-", IRR rounding, all-zero repayment rows dropped).
// The one series the doc doesn't carry (average gross yield per origination
// month, read off the loan tape in the PBIX) comes from /mozzeno-loanbook.
//
// The PBIX repeats the same five pages once per segment (Consumer Insured,
// SME Insured, Consumer Uninsured); here each of those visuals is one tab
// with a <SegmentToggle>, same pattern as the Lend tabs. Not carried over:
// the PBIX's hidden Projected Cashflow page and its Glossary/Disclaimer
// text pages.
import React, { useEffect, useMemo, useState } from 'react';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { TabView, TabPanel } from 'primereact/tabview';
import axiosWithAuth from '../../helpers/AxiosHelper';
import { CHART_COLORS, cyclePalette, BASE_OPTIONS, chartRemountKey, formatLegendText } from './chart_theme';
import LoadingState from '../common/LoadingState';
import EmptyState from '../common/EmptyState';
import { ChartCard, OverviewTable, MatrixTable, PortfolioStatsTable } from './LendReportSections';
import './platform_report.css';

// PBIX page order. Keys are the segment suffixes mozzeno_performance_report
// uses in its own field names (e.g. 15_general_overview_consumer_insured).
const SEGMENTS = ['consumer_insured', 'sme', 'consumer_uninsured'];
const SEGMENT_LABELS = { consumer_insured: 'Consumer Insured', sme: 'SME Insured', consumer_uninsured: 'Consumer Uninsured' };
// Read at render time - CHART_COLORS.categorical is remapped in place for
// dark mode (chart_theme.js's applyChartColorsTheme).
const segmentColor = (segment) => CHART_COLORS.categorical[SEGMENTS.indexOf(segment)];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// 7_loan_origination_*/9_repayment_behaviour_* key months as "April-2017"
// (and arrive in alphabetical, not chronological, order) - "2017-04".
function monthYrToIso(monthYr) {
    const [name, year] = String(monthYr).split('-');
    const month = MONTH_NAMES.indexOf(name) + 1;
    return month ? `${year}-${String(month).padStart(2, '0')}` : String(monthYr);
}

// Fraction (number or numeric string, e.g. "0.0031") -> "0.31%"; null/"" -> "".
function formatFractionPct(value, digits = 2) {
    if (value === null || value === undefined || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? `${(n * 100).toFixed(digits)}%` : String(value);
}

function SegmentToggle({ value, onChange }) {
    const options = SEGMENTS.map((s) => ({ label: SEGMENT_LABELS[s], value: s }));
    return <SelectButton value={value} options={options} onChange={(e) => (e.value ? onChange(e.value) : null)} className="mb-3 pr-segment-toggle" />;
}

const LEGEND_BOTTOM = { legend: { position: 'bottom', labels: { boxWidth: 12 } } };
const PCT_TICKS = { ticks: { callback: (v) => `${v}%` } };

// ============================================================================
// Data shaping - display-only transforms the PBIX's own Power Query applies
// ============================================================================

// 15_general_overview_*: "nan" -> "-" (a substring replace, so "nan%"
// becomes "-%" as in the PBIX) and the IRR value rounded to 2 decimals.
// Labels stay as-is: the PBIX only relabels the literal "Avg. Interest Rate
// in June" (to "Avg. Gross Yield in June"), which no other month matches -
// the reference PDF (September) shows "Avg. Interest Rate in September".
function shapeGeneralOverview(rows) {
    return (rows || []).map((row) => {
        const rawLabel = String(row['Most Recent Indicators'] || '');
        const label = rawLabel === 'Avg. Interest Rate in June' ? 'Avg. Gross Yield in June' : rawLabel;
        let value = String(row.values ?? '').replaceAll('nan', '-');
        if (label === 'Internal Rate of Return' && typeof value === 'string' && value.includes('%')) {
            const n = parseFloat(value);
            if (Number.isFinite(n)) value = `${Math.round(n * 100) / 100}%`;
        }
        return { 'Most Recent Indicators': label, values: value };
    });
}

// 11_selected_active_portfolio_*: "Avg. Interest rate" -> "Avg. Coupon Rate".
function shapeActivePortfolio(rows) {
    return (rows || []).map((row) => ({ ...row, index: row.index === 'Avg. Interest rate' ? 'Avg. Coupon Rate' : row.index }));
}

// basic_overview - the PBIX table is sorted by Particulars.
function shapeBasicOverview(rows) {
    return [...(rows || [])].sort((a, b) => String(a.Particulars).localeCompare(String(b.Particulars))).map((row) => ({ Particulars: row.Particulars, values: typeof row.values === 'number' ? row.values.toLocaleString('en-US') : row.values }));
}

// 12_monthly_return_matrix_*: raw fraction strings -> percentages, rows
// sorted by year (the PBIX sorts ascending too).
function shapeReturnMatrix(rows) {
    return [...(rows || [])]
        .sort((a, b) => String(a.Year).localeCompare(String(b.Year)))
        .map((row) => {
            const out = { Year: row.Year };
            MONTH_ABBR.forEach((m) => {
                out[m] = formatFractionPct(row[m]);
            });
            out.Sum = formatFractionPct(row.Sum);
            return out;
        });
}

// 10_coupons_defaultrates_vintage_*: the PBIX table shows the latest six
// vintage years, rows sorted by label.
function shapeCoupons(rows) {
    if (!rows?.length) return rows;
    const years = Object.keys(rows[0]).filter((k) => /^\d{4}$/.test(k)).sort().slice(-6);
    return [...rows]
        .sort((a, b) => String(a.year).localeCompare(String(b.year)))
        .map((row) => ({ year: row.year, ...Object.fromEntries(years.map((y) => [y, row[y]])) }));
}

// 13_risk_details_df_*: the PBIX table expands exactly these columns - the
// doc also carries a "3 year" column the PBIX never showed.
const RISK_COLUMNS = ['Parameters', '30 days', '90 days', 'YTD', '1 year', 'Since Inception'];
function shapeRiskDetails(rows) {
    return (rows || []).map((row) => Object.fromEntries(RISK_COLUMNS.map((c) => [c, row[c]])));
}

function originationByMonth(rows) {
    return (rows || [])
        .map((row) => ({ month: monthYrToIso(row.month_yr), amount: row.loan_original_amount || 0 }))
        .sort((a, b) => a.month.localeCompare(b.month));
}

function originationByYear(rows) {
    const byYear = new Map();
    originationByMonth(rows).forEach(({ month, amount }) => {
        const year = month.slice(0, 4);
        byYear.set(year, (byYear.get(year) || 0) + amount);
    });
    return byYear;
}

// ============================================================================
// Charts
// ============================================================================

function BarChart({ labels, datasets, stacked = false, yPercent = false, max }) {
    const data = useMemo(() => ({ labels, datasets }), [labels, datasets]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: {
                x: { stacked, ticks: { maxRotation: 90, minRotation: labels.length > 24 ? 90 : 0 } },
                y: { stacked, ...(max ? { max } : {}), ...(yPercent ? PCT_TICKS : {}) },
            },
            plugins: datasets.length > 1 ? LEGEND_BOTTOM : { legend: { display: false } },
        }),
        [stacked, yPercent, max, labels.length, datasets.length]
    );
    if (!labels.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="bar" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function LineChart({ labels, datasets, xTitle, yPercent = true }) {
    const data = useMemo(() => ({ labels, datasets }), [labels, datasets]);
    const options = useMemo(
        () => ({
            ...BASE_OPTIONS,
            scales: { x: { type: 'category', ...(xTitle ? { title: { display: true, text: xTitle } } : {}) }, y: yPercent ? PCT_TICKS : {} },
            plugins: datasets.length > 1 ? LEGEND_BOTTOM : { legend: { display: false } },
        }),
        [xTitle, yPercent, datasets.length]
    );
    if (!labels.length || !datasets.length) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="line" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function lineDataset(label, color, values) {
    return { label, borderColor: color, backgroundColor: color, data: values, fill: false, pointRadius: 0, spanGaps: true };
}

// "Average Gross Yield of Monthly Loan Origination Vintages" - `segment` is
// one of SEGMENTS, or "all" for the Summary page's unfiltered chart.
function GrossYieldChart({ loanbook, loanbookLoading, segment }) {
    const rows = useMemo(() => (loanbook?.gross_yield_by_month || []).filter((r) => r.segment === segment && typeof r.gross_yield === 'number'), [loanbook, segment]);
    const labels = useMemo(() => rows.map((r) => r.month), [rows]);
    const datasets = useMemo(
        () => [lineDataset('Average Gross Yield', segment === 'all' ? CHART_COLORS.categorical[0] : segmentColor(segment), rows.map((r) => r.gross_yield * 100))],
        [rows, segment]
    );
    if (loanbookLoading) return <LoadingState label="Loading loanbook" />;
    return <LineChart labels={labels} datasets={datasets} />;
}

function OriginationByYearChart({ doc, segments }) {
    const { labels, datasets } = useMemo(() => {
        const bySegment = segments.map((s) => ({ segment: s, byYear: originationByYear(doc[`7_loan_origination_${s}`]) }));
        const years = [...new Set(bySegment.flatMap((s) => [...s.byYear.keys()]))].sort();
        return {
            labels: years,
            datasets: bySegment.map((s) => ({ label: SEGMENT_LABELS[s.segment], backgroundColor: segmentColor(s.segment), data: years.map((y) => s.byYear.get(y) || 0) })),
        };
    }, [doc, segments]);
    return <BarChart labels={labels} datasets={datasets} stacked />;
}

// The PBIX's Consumer Insured month chart filters out the years before 2020;
// the other segments show their full history.
const MONTH_CHART_START = { consumer_insured: '2020-01' };

function OriginationByMonthChart({ rows, segment }) {
    const points = useMemo(() => originationByMonth(rows).filter((p) => !MONTH_CHART_START[segment] || p.month >= MONTH_CHART_START[segment]), [rows, segment]);
    const labels = useMemo(() => points.map((p) => p.month), [points]);
    const datasets = useMemo(() => [{ label: 'Origination Volume', backgroundColor: segmentColor(segment), data: points.map((p) => p.amount) }], [points, segment]);
    return <BarChart labels={labels} datasets={datasets} />;
}

function TicketSizeChart({ rows, segment }) {
    const safeRows = useMemo(() => [...(rows || [])].sort((a, b) => a.year - b.year), [rows]);
    const labels = useMemo(() => safeRows.map((r) => String(r.year)), [safeRows]);
    const datasets = useMemo(() => [{ label: 'Average Ticket Size', backgroundColor: segmentColor(segment), data: safeRows.map((r) => r.loan_original_amount) }], [safeRows, segment]);
    return <BarChart labels={labels} datasets={datasets} />;
}

// 20_outstanding_buckets_*: same buckets as the reference report's pie and
// table. Capital in Workout isn't listed - Expected Recovery + Expected Loss
// split it exactly, so showing it as well would double-count it.
const DAYS_LATE_BUCKETS = ['Performing', '1 - 30', '31 - 60', '61 - 90', '91 - 120', '120+', 'Expected Recovery', 'Expected Loss'];
const DAYS_LATE_PIE_BUCKETS = DAYS_LATE_BUCKETS;

function DaysLatePieChart({ buckets }) {
    const data = useMemo(
        () => ({
            labels: DAYS_LATE_PIE_BUCKETS.map((b) => formatLegendText(b)),
            datasets: [{ data: DAYS_LATE_PIE_BUCKETS.map((b) => buckets?.[b] || 0), backgroundColor: cyclePalette(CHART_COLORS.categorical, DAYS_LATE_PIE_BUCKETS.length) }],
        }),
        [buckets]
    );
    const options = useMemo(() => ({ ...BASE_OPTIONS, plugins: { legend: { position: 'right', labels: { boxWidth: 12 } } } }), []);
    if (!buckets) return <EmptyState icon="pi-inbox" title="No data" compact />;
    return <Chart key={chartRemountKey(data)} type="pie" data={data} options={options} style={{ height: '100%', width: '100%' }} />;
}

function DaysLateTable({ buckets }) {
    if (!buckets) return <EmptyState icon="pi-inbox" title="No data" compact />;
    const rows = DAYS_LATE_BUCKETS.filter((b) => b in buckets).map((b) => ({
        bucket: b,
        amount: Number(buckets[b] || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    }));
    return (
        <div className="pr-table-frame mb-3">
            <DataTable value={rows} size="small" stripedRows>
                <Column field="bucket" header="Days Late" bodyClassName="pr-cell-strong" />
                <Column field="amount" header="Outstanding" align="right" />
            </DataTable>
        </div>
    );
}

// 5_interest_rate_distribution_*: one column per vintage year (fractions),
// one row per rate bucket - grouped bar, one series per year.
function InterestDistributionChart({ rows }) {
    const { labels, datasets } = useMemo(() => {
        const safeRows = rows || [];
        const years = safeRows.length ? Object.keys(safeRows[0]).filter((k) => k !== 'Interest_rate_category') : [];
        const colors = cyclePalette(CHART_COLORS.categorical, years.length);
        return {
            labels: safeRows.map((r) => r.Interest_rate_category),
            datasets: years.map((year, i) => ({ label: year, backgroundColor: colors[i], data: safeRows.map((r) => (Number(r[year]) || 0) * 100) })),
        };
    }, [rows]);
    return <BarChart labels={labels} datasets={datasets} yPercent />;
}

// vintage_*'s {segment}_vintage: one row per month since origination (row
// position = month), one column per vintage year, cumulative loss rate as a
// fraction - null past a vintage's own age.
function LossRateVintageChart({ rows }) {
    const { labels, datasets } = useMemo(() => {
        const safeRows = rows || [];
        const years = safeRows.length ? Object.keys(safeRows[0]) : [];
        const colors = cyclePalette(CHART_COLORS.categorical, years.length);
        // Trim the trailing all-null months (beyond the oldest vintage's age).
        const hasValue = (row) => years.some((y) => typeof row[y] === 'number');
        let lastRow = safeRows.length - 1;
        while (lastRow >= 0 && !hasValue(safeRows[lastRow])) lastRow -= 1;
        const trimmed = safeRows.slice(0, lastRow + 1);
        return {
            labels: trimmed.map((_, i) => String(i)),
            datasets: years.map((year, i) => lineDataset(year, colors[i], trimmed.map((r) => (typeof r[year] === 'number' ? r[year] * 100 : null)))),
        };
    }, [rows]);
    return <LineChart labels={labels} datasets={datasets} xTitle="Months since origination" />;
}

// 9_repayment_behaviour_*: last 24 origination months, 100%-stacked. Rows
// where all three shares are 0 are dropped first, as the PBIX's query does.
function RepaymentStatusChart({ rows }) {
    const recent = useMemo(
        () =>
            (rows || [])
                .filter((r) => !(r.Repaid === 0 && r.current === 0 && r.defaulted === 0))
                .map((r) => ({ ...r, month: monthYrToIso(r.month_yr) }))
                .sort((a, b) => a.month.localeCompare(b.month))
                .slice(-24),
        [rows]
    );
    const labels = useMemo(() => recent.map((r) => r.month), [recent]);
    const datasets = useMemo(
        () => [
            { label: 'Repaid', backgroundColor: CHART_COLORS.categorical[1], data: recent.map((r) => (r.Repaid || 0) * 100) },
            { label: 'Loss due to Default', backgroundColor: '#e0a458', data: recent.map((r) => (r.defaulted || 0) * 100) },
            { label: 'Current', backgroundColor: CHART_COLORS.categorical[0], data: recent.map((r) => (r.current || 0) * 100) },
        ],
        [recent]
    );
    return <BarChart labels={labels} datasets={datasets} stacked yPercent max={100} />;
}

// index_*'s {segment}_index: Index (base 1000) per month, up to the report
// month (the series can run past it).
function CumulativePerformanceChart({ rows, reportMonth, segment }) {
    const clean = useMemo(() => (rows || []).filter((r) => typeof r.Index === 'number' && (!reportMonth || r.Date <= reportMonth)), [rows, reportMonth]);
    const labels = useMemo(() => clean.map((r) => r.Date), [clean]);
    const datasets = useMemo(() => [lineDataset('Index', segmentColor(segment), clean.map((r) => r.Index))], [clean, segment]);
    return <LineChart labels={labels} datasets={datasets} yPercent={false} />;
}

// 14_twelve_month_rollong_returns_* (sic): {"YYYY-MM": fraction} - the last
// 12 months up to the report month, as on the reference report.
function RollingReturnsChart({ series, reportMonth, segment }) {
    const points = useMemo(
        () =>
            Object.entries(series || {})
                .filter(([month, v]) => typeof v === 'number' && (!reportMonth || month <= reportMonth))
                .sort(([a], [b]) => a.localeCompare(b))
                .slice(-12),
        [series, reportMonth]
    );
    const labels = useMemo(() => points.map(([m]) => m), [points]);
    const datasets = useMemo(() => [{ label: '12 Month Rolling Return', backgroundColor: segmentColor(segment), data: points.map(([, v]) => v * 100) }], [points, segment]);
    return <BarChart labels={labels} datasets={datasets} yPercent />;
}

// ============================================================================
// Sections - each owns its own segment toggle, same as the Lend tabs
// ============================================================================

function SegmentSection({ render }) {
    const [segment, setSegment] = useState(SEGMENTS[0]);
    return (
        <>
            <SegmentToggle value={segment} onChange={setSegment} />
            {render(segment)}
        </>
    );
}

export function buildTabsForMozzenoDoc(doc, loanbook, loanbookLoading) {
    if (!doc) return [];
    const reportMonth = doc.report_date?.slice(0, 7);
    return [
        {
            key: 'summary',
            header: 'Summary',
            content: (
                <>
                    <div className="grid">
                        <div className="col-12 md:col-5">
                            <div className="pr-card h-full">
                                <h5 className="pr-card-title">General Overview</h5>
                                <OverviewTable rows={shapeBasicOverview(doc.basic_overview)} labelKey="Particulars" />
                            </div>
                        </div>
                        <div className="col-12 md:col-7">
                            <ChartCard title="Origination Volume by Year" height={300}>
                                <OriginationByYearChart doc={doc} segments={SEGMENTS} />
                            </ChartCard>
                        </div>
                    </div>
                    <ChartCard title="Average Gross Yield of Monthly Loan Origination Vintages" height={320}>
                        <GrossYieldChart loanbook={loanbook} loanbookLoading={loanbookLoading} segment="all" />
                    </ChartCard>
                    <div className="text-color-secondary text-sm">
                        Note: 1) All calculations regarding loss rates, default rates, and return rates in this report assume a 0% recovery rate unless actual recovery received is specified. 2) All calculations in this report are based on the loan status provided by the Originator.
                    </div>
                </>
            ),
        },
        {
            key: 'overview',
            header: 'General Overview',
            content: (
                <div className="grid">
                    {SEGMENTS.map((s) => (
                        <div key={s} className="col-12 md:col-4">
                            <div className="pr-card h-full">
                                <h5 className="pr-card-title">{SEGMENT_LABELS[s]}</h5>
                                <OverviewTable rows={shapeGeneralOverview(doc[`15_general_overview_${s}`])} />
                            </div>
                        </div>
                    ))}
                </div>
            ),
        },
        {
            key: 'origination',
            header: 'Origination Volume',
            content: (
                <SegmentSection
                    render={(s) => (
                        <>
                            <ChartCard title={`Origination Volume by Year - ${SEGMENT_LABELS[s]}`} height={320}>
                                <OriginationByYearChart doc={doc} segments={[s]} />
                            </ChartCard>
                            <ChartCard title={`Origination Volume by Month - ${SEGMENT_LABELS[s]}`} height={360}>
                                <OriginationByMonthChart rows={doc[`7_loan_origination_${s}`]} segment={s} />
                            </ChartCard>
                        </>
                    )}
                />
            ),
        },
        {
            key: 'yield_ticket',
            header: 'Gross Yield & Ticket Size',
            content: (
                <SegmentSection
                    render={(s) => (
                        <>
                            <ChartCard title={`Average Gross Yield of Monthly Loan Origination Vintages - ${SEGMENT_LABELS[s]}`} height={320}>
                                <GrossYieldChart loanbook={loanbook} loanbookLoading={loanbookLoading} segment={s} />
                            </ChartCard>
                            <ChartCard title={`Average Ticket Size - ${SEGMENT_LABELS[s]}`} height={320}>
                                <TicketSizeChart rows={doc[`6_ticket_size_${s}`]} segment={s} />
                            </ChartCard>
                        </>
                    )}
                />
            ),
        },
        {
            key: 'days_late',
            header: 'Days Late',
            content: (
                <SegmentSection
                    render={(s) => (
                        <div className="grid">
                            <div className="col-12 md:col-7">
                                <ChartCard title={`Outstanding Amount by Days Late - ${SEGMENT_LABELS[s]}`} height={340}>
                                    <DaysLatePieChart buckets={doc[`20_outstanding_buckets_${s}`]} />
                                </ChartCard>
                            </div>
                            <div className="col-12 md:col-5">
                                <DaysLateTable buckets={doc[`20_outstanding_buckets_${s}`]} />
                            </div>
                        </div>
                    )}
                />
            ),
        },
        {
            key: 'interest_distribution',
            header: 'Interest Rate Distribution',
            content: (
                <SegmentSection
                    render={(s) => (
                        <ChartCard title={`Interest Rate Distribution (Gross Yield) - ${SEGMENT_LABELS[s]}`} height={340}>
                            <InterestDistributionChart rows={doc[`5_interest_rate_distribution_${s}`]} />
                        </ChartCard>
                    )}
                />
            ),
        },
        {
            key: 'loss_vintage',
            header: 'Loss Rates per Vintage',
            content: (
                <SegmentSection
                    render={(s) => (
                        <ChartCard title={`Loss Rates per Vintage - ${SEGMENT_LABELS[s]}`} height={380}>
                            <LossRateVintageChart rows={doc.vintage?.[`${s}_vintage`]} />
                        </ChartCard>
                    )}
                />
            ),
        },
        {
            key: 'coupons_portfolio_stats',
            header: 'Coupons & Portfolio Stats',
            content: (
                <>
                    {SEGMENTS.map((s) => (
                        <React.Fragment key={s}>
                            <h5 className="pr-subheading mb-2">Default Rates &amp; Coupons by Vintage - {SEGMENT_LABELS[s]}</h5>
                            <MatrixTable rows={shapeCoupons(doc[`10_coupons_defaultrates_vintage_${s}`])} labelCol="year" labelHeader="Particulars" />
                        </React.Fragment>
                    ))}
                    <h5 className="pr-subheading mb-2">Selected Active Portfolio Statistics since Launch</h5>
                    <PortfolioStatsTable statsBySegment={Object.fromEntries(SEGMENTS.map((s) => [SEGMENT_LABELS[s], shapeActivePortfolio(doc[`11_selected_active_portfolio_${s}`])]))} />
                </>
            ),
        },
        {
            key: 'return_matrix',
            header: 'Monthly Return Matrix',
            content: <SegmentSection render={(s) => <MatrixTable rows={shapeReturnMatrix(doc[`12_monthly_return_matrix_${s}`])} labelCol="Year" labelHeader={SEGMENT_LABELS[s]} />} />,
        },
        {
            key: 'repayment_status',
            header: 'Repayment Status',
            content: (
                <SegmentSection
                    render={(s) => (
                        <ChartCard title={`Repayment Status by Vintage last 24 Months - ${SEGMENT_LABELS[s]}`} height={380}>
                            <RepaymentStatusChart rows={doc[`9_repayment_behaviour_${s}`]} />
                        </ChartCard>
                    )}
                />
            ),
        },
        {
            key: 'risk_metrics',
            header: 'Performance & Risk Metrics',
            content: (
                <SegmentSection
                    render={(s) => (
                        <>
                            <MatrixTable rows={shapeRiskDetails(doc[`13_risk_details_df_${s}`])} labelCol="Parameters" labelHeader={SEGMENT_LABELS[s]} />
                            <ChartCard title={`Cumulative Portfolio Performance - ${SEGMENT_LABELS[s]}`} height={340}>
                                <CumulativePerformanceChart rows={doc.index?.[`${s}_index`]} reportMonth={reportMonth} segment={s} />
                            </ChartCard>
                        </>
                    )}
                />
            ),
        },
        {
            key: 'rolling_returns',
            header: 'Rolling Returns',
            content: (
                <SegmentSection
                    render={(s) => (
                        <ChartCard title={`12 Month Rolling Returns - ${SEGMENT_LABELS[s]}`} height={340}>
                            <RollingReturnsChart series={doc[`14_twelve_month_rollong_returns_${s}`]} reportMonth={reportMonth} segment={s} />
                        </ChartCard>
                    )}
                />
            ),
        },
    ];
}

// Same shape as LendDocTabs: own active-tab state with lazy tab rendering,
// plus the one /mozzeno-loanbook fetch per report_date (the gross-yield
// series), reported through the parent's reference-counted
// onLoadingStart/onLoadingEnd pair.
export default function MozzenoDocTabs({ doc, onLoadingStart = () => {}, onLoadingEnd = () => {} }) {
    const [activeTab, setActiveTab] = useState(0);
    const [loanbook, setLoanbook] = useState(null);
    const [loanbookLoading, setLoanbookLoading] = useState(false);

    useEffect(() => {
        if (!doc?.report_date) return;
        let cancelled = false;
        setLoanbookLoading(true);
        onLoadingStart();
        axiosWithAuth
            .get('/mozzeno-loanbook', { params: { report_date: doc.report_date } })
            .then((res) => {
                if (!cancelled) setLoanbook(res.data);
            })
            .catch(() => {
                if (!cancelled) setLoanbook(null);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoanbookLoading(false);
                    onLoadingEnd();
                }
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doc?.report_date]);

    const tabs = buildTabsForMozzenoDoc(doc, loanbook, loanbookLoading);
    if (!doc) return <LoadingState label="Loading report" />;
    return (
        <TabView scrollable className="pr-report-tabs" activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            {tabs.map((tab, i) => (
                <TabPanel key={tab.key} header={tab.header}>
                    {activeTab === i && tab.content}
                </TabPanel>
            ))}
        </TabView>
    );
}
